import { ref, computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useSurveyStore } from "@stores/survey/survey";
import { useConstantsStore } from "@stores/constants";
import useMyFetch from "@stores/useMyFetch";
import { useDeltaTracking, createSaveAll, readErrorMessages, type SaveErrorPayload } from "@utils/composables/useDeltaTracking";
import prepareCards from "@stores/survey/prepareCards";
import type { Card } from "@/types";
import { captureEvent } from "@/lib/analytics";

export const useCardsStore = defineStore("cards", () => {
  const { token: surveyToken } = storeToRefs(useSurveyStore());
  const { getCardTypeGroupName } = useConstantsStore();
  const delta = useDeltaTracking();

  const cards = ref<Card[]>([]);
  const selectedToken = ref<string | null>(null);

  const basePath = computed(() => `surveys/${surveyToken.value}/cards`);
  const hasUnsavedChanges = delta.hasUnsavedChanges;

  const fetch = async () => {
    if (!surveyToken.value) return;
    const { response, data } = await useMyFetch(basePath.value).get().json<Card[]>();
    cards.value = response.value?.ok ? prepareCards(data.value ?? [], getCardTypeGroupName) : [];
    delta.reset();
  };

  const hydrate = (list: Card[]) => {
    cards.value = prepareCards(list, getCardTypeGroupName);
    delta.reset();
  };

  const findByToken = (token: string) => cards.value.find((card) => card.token === token) ?? null;

  const findIndex = (token: string) => cards.value.findIndex((card) => card.token === token);

  const predecessorToken = (token: string) => {
    const index = findIndex(token);
    return index > 0 ? cards.value[index - 1].token : null;
  };

  const addCard = (card: Card, afterToken: string | null = null) => {
    const anchorIndex = afterToken ? findIndex(afterToken) : -1;
    const index = anchorIndex === -1 ? cards.value.length - 1 : anchorIndex;
    if (!card.section_token) card.section_token = cards.value[index]?.section_token ?? null;
    cards.value.splice(index + 1, 0, card);
    delta.markNew(card.token);
    selectedToken.value = card.token;
  };

  const updateCard = (token: string) => delta.markDirty(token);

  const removeCard = (token: string) => {
    const index = findIndex(token);
    if (index === -1) return;
    cards.value.splice(index, 1);
    delta.markDeleted(token);
    if (selectedToken.value === token) selectedToken.value = null;
  };

  const moveCard = (token: string, afterToken: string | null, sectionToken: string | null) => {
    const index = findIndex(token);
    if (index === -1) return;
    const [card] = cards.value.splice(index, 1);
    card.section_token = sectionToken;
    card.section_id = undefined;
    const targetIndex = afterToken ? findIndex(afterToken) : -1;
    cards.value.splice(targetIndex + 1, 0, card);
    delta.markMoved(token, { after_token: afterToken, section_token: sectionToken });
  };

  const resortBySections = (sectionTokens: (string | null | undefined)[]) => {
    const rank = new Map(sectionTokens.map((token, index) => [token ?? null, index]));
    cards.value = [...cards.value].sort(
      (a, b) => (rank.get(a.section_token ?? null) ?? 0) - (rank.get(b.section_token ?? null) ?? 0),
    );
  };

  const cardPayload = (card: Card) => ({
    token: card.token,
    name: card.name,
    type: card.type,
    section_token: card.section_token,
    is_admin: card.is_admin,
    is_charts: card.is_charts,
    is_for_analytics: card.is_for_analytics,
    is_recode_auto: card.is_recode_auto,
    custom_base: card.custom_base,
    specs: card.specs,
  });

  const saveCard = async (
    token: string,
    { withProgress = false, clientId = null }: { withProgress?: boolean; clientId?: string | null } = {},
  ) => {
    const card = findByToken(token);
    if (!card) return { ok: false, errors: [] };
    const isNew = delta.newTokens.value.has(token);
    const request = isNew
      ? useMyFetch(basePath.value)
          .post({
            card: { ...cardPayload(card), after_token: predecessorToken(token) },
            with_progress: withProgress,
            client_id: clientId,
          })
          .json<SaveErrorPayload & Partial<Card>>()
      : useMyFetch(`${basePath.value}/${token}`)
          .patch({ card: cardPayload(card), with_progress: withProgress, client_id: clientId })
          .json<SaveErrorPayload & Partial<Card>>();
    const { response, data, error } = await request;
    const ok = response.value?.ok ?? false;
    if (ok) {
      delta.newTokens.value.delete(token);
      delta.dirtyTokens.value.delete(token);
      if (data.value && typeof data.value === "object" && "token" in data.value) {
        Object.assign(card, data.value);
      }
    }
    return { ok, card: ok ? card : null, errors: ok ? [] : readErrorMessages(data.value, error.value) };
  };

  const persistAll = createSaveAll(delta, findIndex, {
    destroy: (token) => useMyFetch(`${basePath.value}/${token}`).delete().json<SaveErrorPayload>(),
    create: (token) =>
      useMyFetch(basePath.value)
        .post({ card: { ...cardPayload(cards.value[findIndex(token)]), after_token: predecessorToken(token) } })
        .json<SaveErrorPayload>(),
    update: (token) =>
      useMyFetch(`${basePath.value}/${token}`)
        .patch({ card: cardPayload(cards.value[findIndex(token)]) })
        .json<SaveErrorPayload>(),
    reorder: (token) =>
      useMyFetch(`${basePath.value}/${token}/reorder`)
        .patch({ after_token: predecessorToken(token), section_token: findByToken(token)?.section_token ?? null })
        .json<SaveErrorPayload>(),
  });

  const saveAll = async () => {
    const created = delta.newTokens.value.size;
    const updated = delta.dirtyTokens.value.size;
    const deleted = delta.deletedTokens.value.length;
    const moved = delta.moves.value.size;
    const errors = await persistAll();

    if (!errors.length && created + updated + deleted + moved) {
      captureEvent("questionnaire_saved", {
        cards_created: created,
        cards_updated: updated,
        cards_deleted: deleted,
        cards_moved: moved,
      });
    }
    return errors;
  };

  return {
    cards,
    selectedToken,
    hasUnsavedChanges,
    newTokens: delta.newTokens,
    fetch,
    hydrate,
    findByToken,
    findIndex,
    addCard,
    updateCard,
    removeCard,
    moveCard,
    resortBySections,
    saveCard,
    saveAll,
    resetDirtyState: delta.reset,
  };
});
